/** Друкована форма декларації з податку на прибуток (+ додатки РІ, АМ, ЗП, АВ, ПН, ВП, якщо є) — ч.1387, ч.1388. */
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PERIOD_LABELS, profitTaxDeclarations } from '@/api/profitTaxDeclarations';
import { orgs } from '@/api/catalogs';
import { DeclarationHeader, MainRows, MarksBlock, RiAppendix, SignaturesBlock } from './ProfitTaxBlank';
import { AmAppendix, PnAppendix, SimpleAppendix, VpAppendix } from './ProfitTaxAppendicesBlank';
import { financialStatements, formsFor } from '@/api/financialStatements';

export default function ProfitTaxDeclarationPrintPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const docQ = useQuery({ queryKey: ['profit-tax-declaration', id], queryFn: () => profitTaxDeclarations.get(id!), enabled: !!id });
  const d = docQ.data;
  const riQ = useQuery({ queryKey: ['profit-tax-ri-rows'], queryFn: profitTaxDeclarations.riRows, staleTime: 3_600_000 });
  const rowsQ = useQuery({ queryKey: ['profit-tax-appendix-rows'], queryFn: profitTaxDeclarations.appendixRows, staleTime: 3_600_000 });
  const orgListQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgListQ.data?.items.find((o) => o.id === d?.organization_id);
  const fsQ = useQuery({ queryKey: ['financial-statement', d?.financial_statement_id], queryFn: () => financialStatements.get(d!.financial_statement_id!), enabled: !!d?.financial_statement_id });
  if (!d) return <div className="card">Завантаження…</div>;
  const a = d.appendix_data ?? {};
  const withRi = !!a.include_ri;
  const extras = [a.include_am && 'АМ', a.include_zp && 'ЗП', a.include_av && 'АВ', (a.pn?.length ?? 0) > 0 && `ПН×${a.pn!.length}`, (a.vp?.length ?? 0) > 0 && `ВП×${a.vp!.length}`].filter(Boolean) as string[];
  const f = d.fields ?? {};
  return (
    <div style={{ background: '#fff', color: '#000', minHeight: '100vh' }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: A4 portrait; margin: 1.2cm; }
          .print-page { page-break-before: always; }
          .print-page:first-of-type { page-break-before: auto; }
          nav, aside, header { display: none !important; }
          body { background: #fff !important; }
        }
        .pt-print table.tch { color: #000; }
        .pt-print table.tch th, .pt-print table.tch td { border: 1px solid #999; }
      `}</style>
      <div className="no-print" style={{ display: 'flex', gap: '0.5rem', padding: '0.6rem 1rem', borderBottom: '1px solid #e2e8f0', alignItems: 'center' }}>
        <b>Друк: декларація з податку на прибуток — {PERIOD_LABELS[d.period_quarter]} {d.period_year}</b>
        <span style={{ color: '#64748b', fontSize: '0.85rem' }}>{[withRi && 'РІ', ...extras].filter(Boolean).length ? `+ додатки: ${[withRi && 'РІ', ...extras].filter(Boolean).join(', ')}` : 'без додатків'}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
          <button className="primary" onClick={() => window.print()}>друкувати</button>
          <button onClick={() => navigate(`/taxation/profit-tax-declarations/${id}`)}>до декларації</button>
        </div>
      </div>
      <div className="pt-print" style={{ maxWidth: 900, margin: '0 auto', padding: '1rem' }}>
        <div className="print-page">
          <DeclarationHeader doc={d} org={org} fields={f} readOnly print />
          <MainRows doc={d} values={d.values ?? {}} readOnly print />
          <MarksBlock appendix={a} fields={f} readOnly print
            statementForms={fsQ.data ? formsFor(fsQ.data.form_set, fsQ.data.period_quarter) : []}
            statementStandards={(fsQ.data?.fields?.standards as 'NPSBO' | 'IFRS' | undefined) ?? null} />
          <SignaturesBlock doc={d} org={org} print />
        </div>
        {withRi && riQ.data && (
          <div className="print-page" style={{ marginTop: '1.5rem' }}>
            <RiAppendix rows={riQ.data} ri={a.ri ?? {}} readOnly print doc={d} org={org} />
          </div>
        )}
        {a.include_am && rowsQ.data && (
          <div className="print-page" style={{ marginTop: '1.5rem' }}>
            <AmAppendix rows={rowsQ.data.am} am={a.am ?? {}} readOnly print doc={d} org={org} />
          </div>
        )}
        {a.include_zp && rowsQ.data && (
          <div className="print-page" style={{ marginTop: '1.5rem' }}>
            <SimpleAppendix rows={rowsQ.data.zp} cells={a.zp ?? {}} readOnly print doc={d} org={org}
              title="Додаток ЗП до рядка 16 ЗП Податкової декларації з податку на прибуток підприємств" subtitle="Зменшення нарахованої суми податку" />
          </div>
        )}
        {a.include_av && rowsQ.data && (
          <div className="print-page" style={{ marginTop: '1.5rem' }}>
            <SimpleAppendix rows={rowsQ.data.av} cells={a.av ?? {}} readOnly print doc={d} org={org}
              title="Додаток АВ до рядка 20 АВ Податкової декларації з податку на прибуток підприємств"
              subtitle="Розрахунок авансового внеску з податку на прибуток підприємств на суму виплачених дивідендів (прирівняних до них платежів)" />
          </div>
        )}
        {(a.pn?.length ?? 0) > 0 && rowsQ.data && (
          <div className="print-page" style={{ marginTop: '1.5rem' }}>
            <PnAppendix rows={rowsQ.data.pn} items={a.pn ?? []} readOnly print doc={d} org={org} />
          </div>
        )}
        {(a.vp?.length ?? 0) > 0 && rowsQ.data && (
          <div className="print-page" style={{ marginTop: '1.5rem' }}>
            <VpAppendix resultRows={rowsQ.data.vp} items={a.vp ?? []} readOnly print year={d.period_year} quarter={d.period_quarter} doc={d} org={org} />
          </div>
        )}
      </div>
    </div>
  );
}
